export type GovernorRuntimeContractMatchMode =
  | 'exact'
  | 'prefix';

export type GovernorRuntimeFallbackMode =
  | 'public-safe'
  | 'login-required'
  | 'authenticated-app'
  | 'command-center-required'
  | 'staff-required'
  | 'not-found';

export interface GovernorRuntimeContract {
  readonly id: string;
  readonly scope: string;
  readonly matchMode: GovernorRuntimeContractMatchMode;
  readonly primaryGovernor: string;
  readonly supportingGovernors: readonly string[];
  readonly fallbackMode: GovernorRuntimeFallbackMode;
  readonly evidenceRequired: boolean;
  readonly verificationRequired: boolean;
  readonly repairCapable: boolean;
  readonly connected: boolean;
  readonly description: string;
}

export interface RuntimeContractRegistrySummary {
  readonly total: number;
  readonly connected: number;
  readonly disconnected: number;
  readonly owned: number;
  readonly unowned: number;
  readonly fullyAccountable: boolean;
}

const contract = (
  value: GovernorRuntimeContract,
): GovernorRuntimeContract =>
  Object.freeze({
    ...value,
    supportingGovernors: Object.freeze([
      ...value.supportingGovernors,
    ]),
  });

const publicRoute = (
  id: string,
  scope: string,
  description: string,
): GovernorRuntimeContract =>
  contract({
    id,
    scope,
    matchMode: 'exact',
    primaryGovernor: 'AX Governor',
    supportingGovernors: [
      'Security/Auth Governor',
      'Verification Governor',
    ],
    fallbackMode: 'public-safe',
    evidenceRequired: true,
    verificationRequired: true,
    repairCapable: false,
    connected: true,
    description,
  });

const signedOutRoute = (
  id: string,
  scope: string,
  description: string,
): GovernorRuntimeContract =>
  contract({
    id,
    scope,
    matchMode: 'exact',
    primaryGovernor: 'AX Governor',
    supportingGovernors: [
      'Security/Auth Governor',
      'Verification Governor',
    ],
    fallbackMode: 'public-safe',
    evidenceRequired: true,
    verificationRequired: true,
    repairCapable: false,
    connected: true,
    description,
  });

const authenticatedRoute = (
  id: string,
  scope: string,
  description: string,
  supportingGovernors: readonly string[] = [],
): GovernorRuntimeContract =>
  contract({
    id,
    scope,
    matchMode: 'exact',
    primaryGovernor: 'AX Governor',
    supportingGovernors: [
      'Security/Auth Governor',
      'Verification Governor',
      ...supportingGovernors,
    ],
    fallbackMode: 'login-required',
    evidenceRequired: true,
    verificationRequired: true,
    repairCapable: false,
    connected: true,
    description,
  });

const staffRoute = (
  id: string,
  scope: string,
  description: string,
): GovernorRuntimeContract =>
  contract({
    id,
    scope,
    matchMode: 'exact',
    primaryGovernor: 'AX Governor',
    supportingGovernors: [
      'Security/Auth Governor',
      'Executive Governor',
      'Verification Governor',
      'Repair Governor',
    ],
    fallbackMode: 'staff-required',
    evidenceRequired: true,
    verificationRequired: true,
    repairCapable: true,
    connected: true,
    description,
  });

const commandCenterRoute = (
  id: string,
  scope: string,
  description: string,
): GovernorRuntimeContract =>
  contract({
    id,
    scope,
    matchMode: 'exact',
    primaryGovernor: 'AX Governor',
    supportingGovernors: [
      'Executive Governor',
      'Security/Auth Governor',
      'Verification Governor',
      'Repair Governor',
      'Learning/Incident Memory Governor',
    ],
    fallbackMode: 'command-center-required',
    evidenceRequired: true,
    verificationRequired: true,
    repairCapable: true,
    connected: true,
    description,
  });

const prefixContract = (
  id: string,
  scope: string,
  fallbackMode: GovernorRuntimeFallbackMode,
  description: string,
): GovernorRuntimeContract =>
  contract({
    id,
    scope,
    matchMode: 'prefix',
    primaryGovernor: 'AX Governor',
    supportingGovernors: [
      'Security/Auth Governor',
      'Verification Governor',
    ],
    fallbackMode,
    evidenceRequired: true,
    verificationRequired: true,
    repairCapable: false,
    connected: true,
    description,
  });

const GOVERNOR_RUNTIME_CONTRACTS:
readonly GovernorRuntimeContract[] =
  Object.freeze([
    publicRoute(
      'route.public-landing',
      '/',
      'Canonical public landing page.',
    ),
    signedOutRoute(
      'route.login',
      '/login',
      'Canonical standalone login.',
    ),
    signedOutRoute(
      'route.login-alias-log-in',
      '/log-in',
      'Compatibility login alias.',
    ),
    signedOutRoute(
      'route.login-alias-signin',
      '/signin',
      'Compatibility sign-in alias.',
    ),
    signedOutRoute(
      'route.signup',
      '/signup',
      'Canonical standalone signup.',
    ),
    signedOutRoute(
      'route.reset-password',
      '/reset-password',
      'Password recovery and reset surface.',
    ),
    signedOutRoute(
      'route.auth-callback',
      '/auth/callback',
      'Authentication callback surface.',
    ),
    publicRoute(
      'route.privacy',
      '/privacy',
      'Public privacy and safety policy.',
    ),
    publicRoute(
      'route.terms',
      '/terms',
      'Public terms of service.',
    ),
    publicRoute(
      'route.invite',
      '/invite',
      'Family invitation entry.',
    ),
    publicRoute(
      'route.join',
      '/join',
      'Compatibility family invitation entry.',
    ),
    prefixContract(
      'route.invite-dynamic',
      '/invite/',
      'public-safe',
      'Parameterized family invitation and claim route.',
    ),
    prefixContract(
      'route.join-dynamic',
      '/join/',
      'public-safe',
      'Parameterized compatibility invitation route.',
    ),
    authenticatedRoute(
      'route.member-application',
      '/app',
      'Canonical authenticated member application.',
    ),
    authenticatedRoute(
      'route.dashboard-alias',
      '/dashboard',
      'Authenticated member-application compatibility alias.',
    ),
    authenticatedRoute(
      'route.settings',
      '/settings',
      'Canonical account, membership, billing, and KinsSpace settings.',
      [
        'Membership Governor',
        'Business/Revenue Governor',
      ],
    ),
    authenticatedRoute(
      'route.account-alias',
      '/account',
      'Authenticated Settings compatibility alias.',
      ['Membership Governor'],
    ),
    authenticatedRoute(
      'route.membership-alias',
      '/membership',
      'Authenticated membership compatibility alias.',
      [
        'Membership Governor',
        'Business/Revenue Governor',
      ],
    ),
    authenticatedRoute(
      'route.graph-intelligence',
      '/graph-intelligence',
      'Authenticated graph intelligence workspace.',
      ['Learning/Incident Memory Governor'],
    ),
    authenticatedRoute(
      'route.native-call',
      '/native-call',
      'Authenticated native call runtime.',
    ),
    prefixContract(
      'route.native-call-dynamic',
      '/native-call/',
      'login-required',
      'Parameterized authenticated native call runtime.',
    ),
    publicRoute(
      'route.call-ended',
      '/call-ended',
      'Governed post-call completion surface.',
    ),
    staffRoute(
      'route.settings-email',
      '/settings/email',
      'Staff SMTP and email settings.',
    ),
    commandCenterRoute(
      'route.admin-health',
      '/admin/health',
      'Command Center system-health surface.',
    ),
    commandCenterRoute(
      'route.system-health',
      '/system-health',
      'Command Center system-health compatibility surface.',
    ),
    staffRoute(
      'route.admin-ai',
      '/admin/ai',
      'Staff AI test sandbox.',
    ),
    staffRoute(
      'route.admin-semantic',
      '/admin/semantic',
      'Staff semantic laboratory.',
    ),
    staffRoute(
      'route.admin-legacy',
      '/admin/legacy',
      'Staff legacy viewer.',
    ),
    staffRoute(
      'route.admin-preservation',
      '/admin/preservation',
      'Staff legacy preservation viewer.',
    ),
    staffRoute(
      'route.admin-integrity',
      '/admin/integrity',
      'Staff integrity administration.',
    ),
    staffRoute(
      'route.admin-keys',
      '/admin/keys',
      'Staff secure-key authority.',
    ),
    staffRoute(
      'route.secure-keys-alias',
      '/secure-keys',
      'Secure-key compatibility alias.',
    ),
    commandCenterRoute(
      'route.governor-command-center',
      '/governor-command-center',
      'Canonical Governor Command Center.',
    ),
    commandCenterRoute(
      'route.governor-alias',
      '/governor',
      'Governor Command Center compatibility alias.',
    ),
    commandCenterRoute(
      'route.governor-registry-alias',
      '/governor-registry',
      'Governor Command Center registry alias.',
    ),
    commandCenterRoute(
      'route.governor-command-alias',
      '/governor-command',
      'Governor Command Center compatibility alias.',
    ),
    commandCenterRoute(
      'route.governor-executive-alias',
      '/governor-executive',
      'Governor Command Center executive alias.',
    ),
  ]);

const normalizeScope = (
  input: string,
): string => {
  const withoutQuery = String(input || '/')
    .trim()
    .split('?')[0]
    .split('#')[0];

  const collapsed =
    withoutQuery.replace(/\/{2,}/g, '/') || '/';

  if (collapsed === '/') return '/';

  return collapsed.endsWith('/')
    ? collapsed.slice(0, -1)
    : collapsed;
};

export function getGovernorRuntimeContracts():
readonly GovernorRuntimeContract[] {
  return GOVERNOR_RUNTIME_CONTRACTS;
}

export function getRuntimeContractRegistrySummary():
RuntimeContractRegistrySummary {
  const contracts = getGovernorRuntimeContracts();

  const connected = contracts.filter(
    (item) => item.connected,
  ).length;

  const owned = contracts.filter(
    (item) => item.primaryGovernor.trim().length > 0,
  ).length;

  const disconnected = contracts.length - connected;
  const unowned = contracts.length - owned;

  return Object.freeze({
    total: contracts.length,
    connected,
    disconnected,
    owned,
    unowned,
    fullyAccountable:
      contracts.length > 0 &&
      disconnected === 0 &&
      unowned === 0,
  });
}

export function matchGovernorRuntimeContractByScope(
  scope: string,
): GovernorRuntimeContract | undefined {
  const normalized = normalizeScope(scope);

  const exact = GOVERNOR_RUNTIME_CONTRACTS.find(
    (item) =>
      item.matchMode === 'exact' &&
      normalizeScope(item.scope) === normalized,
  );

  if (exact) return exact;

  return GOVERNOR_RUNTIME_CONTRACTS
    .filter(
      (item) =>
        item.matchMode === 'prefix' &&
        (
          normalized === normalizeScope(item.scope) ||
          normalized.startsWith(
            `${normalizeScope(item.scope)}/`,
          )
        ),
    )
    .sort(
      (a, b) =>
        normalizeScope(b.scope).length -
        normalizeScope(a.scope).length,
    )[0];
}
